import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader/';
import { MobileBackButton } from '@components/DrilldownHeader/DrilldownHeader.style';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import Icon from '@components/Icon/Icon';

import * as ROUTES from '@constants/routes';

import { useGetImagingStudy } from '@hooks/api/imaging-study';
import { useAppLayout } from '@hooks/useAppLayout';

import { constructUrl } from '@utils/url';

import {
  DicomViewerHeader,
  DicomViewerIFrame,
  DicomViewerTitle,
} from './DicomViewer.style';

const messages = defineMessages({
  title: {
    id: 'dicomStudyData.study',
    defaultMessage: 'Study',
  },
  subtitle: {
    id: 'dicomViewer.subtitle',
    defaultMessage: 'Image Viewer',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
});

interface DicomViewerProps {
  studyId?: string;
  reportId?: string;
  isSharing?: boolean;
  patientId?: string;
}

const DicomViewer: React.FC<DicomViewerProps> = ({
  reportId = '',
  studyId = '',
  isSharing = false,
  patientId,
}) => {
  const { formatMessage } = useIntl();
  const { setTitle } = useAppLayout();

  const { data, loading } = useGetImagingStudy({
    record_id: studyId,
    ...(isSharing && { patient_id: patientId }),
  });

  useEffect(() => {
    if (!studyId && !loading && !isSharing) {
      navigate(`${ROUTES.IMAGING_TESTS}${reportId}`);
    }
    setTitle(data?.studyName ?? formatMessage(messages.title));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.studyName, reportId, studyId, loading]);

  const viewerUrl = data?.dicomViewerToken
    ? constructUrl(new URL(process.env.GATSBY_DICOM_VIEWER_URL ?? ''), '/', {
        token: data.dicomViewerToken,
      })
    : null;

  return (
    <>
      {!isSharing && (
        <DicomViewerHeader>
          <MobileBackButton
            style={{ position: 'absolute', top: 11, left: 10 }}
            to={`${ROUTES.IMAGING_TESTS}${reportId}`}
          >
            <Icon
              clickable
              color="primary.contrastText"
              name="chevron-down"
              rotation={90}
              style={{ marginRight: 2 }}
              type="far"
            />
            {formatMessage(messages.back)}
          </MobileBackButton>

          <DicomViewerTitle>{data?.studyName}</DicomViewerTitle>
        </DicomViewerHeader>
      )}

      <ContentLoader loading={loading}>
        {viewerUrl ? (
          <DicomViewerIFrame
            isSharing={isSharing}
            src={viewerUrl.href}
            title={`DICOM Viewer ${data?.studyName}`}
          />
        ) : null}
      </ContentLoader>
    </>
  );
};

export default withErrorBoundary(DicomViewer, {
  FallbackComponent: ErrorBoundaryFallback,
});
